import { useEffect, useRef, useState } from 'react';

export type UseDebouncedValueOptions<T> = {
  /** Runs only after the debounce delay (not on the immediate first sync). */
  onDebouncedCommit?: (value: T) => void;
};

/**
 * Returns `value` debounced by `delayMs`. The first update is applied immediately
 * so the initial mount does not wait for the delay.
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs: number,
  options?: UseDebouncedValueOptions<T>
): T {
  const [debounced, setDebounced] = useState(value);
  const isFirst = useRef(true);
  const onCommitRef = useRef(options?.onDebouncedCommit);

  useEffect(() => {
    onCommitRef.current = options?.onDebouncedCommit;
  }, [options?.onDebouncedCommit]);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      setDebounced(value);
      return;
    }
    const id = window.setTimeout(() => {
      setDebounced(value);
      onCommitRef.current?.(value);
    }, delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}
